import React, {forwardRef, useEffect, useRef} from "react";
import {Box, BoxProps, Button, TextInput} from "@mantine/core";
import {modals} from "@mantine/modals";
import {IconFileText, IconMarkdown} from "@tabler/icons-react";

import {MarkdownEditor} from "./MarkdownEditor";
import classes from "./EventDetailInput.module.css";
import cx from "clsx";
import {EventFormat} from "@/common/types";

interface EventDetailInputProps extends BoxProps {
    format?: string
    details: string
    isPlaying: boolean
    onDetailChange: (format: EventFormat, text: string) => void
    onSubmit?: () => void
    onPauseRequest?: () => void
    showLabel?: boolean
    readonly?: boolean
}

const getMarkdownSummary = (markdown: string): string => {
    if (!markdown) {
        return "";
    }
    const stripped = markdown
        .replace(/#{1,6}\s/g, "")
        .replace(/\*\*|__/g, "")
        .replace(/\*|_/g, "")
        .replace(/`{1,3}[^`]*`{1,3}/g, "")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/!\[([^\]]*)\]\([^)]+\)/g, "")
        .replace(/\n+/g, " ")
        .trim();
    return stripped;
};

export const EventDetailInput = forwardRef<HTMLDivElement, EventDetailInputProps>(({
                                                                                       format,
                                                                                       showLabel = true,
                                                                                       details,
                                                                                       isPlaying,
                                                                                       onDetailChange,
                                                                                       onSubmit,
                                                                                       onPauseRequest,
                                                                                       readonly = false,
                                                                                       ...others
                                                                                   }, ref) => {
    const isMarkdown = format === EventFormat.MARKDOWN;
    const inputRef = useRef<HTMLInputElement>(null);

    // Sync the input's DOM value when an external change to details / format / readonly
    // diverges from what's currently shown. Skipping the sync during normal typing preserves
    // the cursor position (controlled `value` would reset the cursor to the end on every keystroke).
    useEffect(() => {
        if (!inputRef.current) return;
        const expected = readonly || isMarkdown ? "" : details;
        if (inputRef.current.value !== expected) {
            inputRef.current.value = expected;
        }
    }, [details, readonly, isMarkdown]);

    const openMarkdownModal = () => {
        modals.open({
            title: "Event Details (Markdown)",
            size: "xl",
            radius: "md",
            centered: true,
            withCloseButton: true,
            children: (
                <Box className={classes.modalContent}>
                    <MarkdownEditor
                        markdown={details}
                        onChange={(newMarkdown) => {
                            // when you save text from the markdown popup it formats it as markdown,
                            // to change back to text delete it in the markdown popup.
                            if (newMarkdown.trim().length > 0) {
                                onDetailChange(EventFormat.MARKDOWN, newMarkdown);
                            } else {
                                onDetailChange(EventFormat.TEXT, newMarkdown);
                            }

                        }}
                        editingMode={readonly ? "none" : "full"}
                        title={<Box>Details</Box>}
                        mdDataTestId="event-details-editor"
                    />
                    <Box mt="md" style={{display: "flex", justifyContent: "flex-end"}}>
                        <Button onClick={() => modals.closeAll()}>Done</Button>
                    </Box>
                </Box>
            )
        });
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (isPlaying && onPauseRequest) {
            onPauseRequest();
        }
        if (isMarkdown || readonly) {
            e.currentTarget.blur();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && onSubmit) {
            onSubmit();
        }
    };

    const handleClick = () => {
        if (isPlaying && onPauseRequest) {
            onPauseRequest();
        }
        if (isMarkdown) {
            openMarkdownModal();
        }
    };

    const handleMarkdownButtonClick = () => {
        if (isPlaying && onPauseRequest) {
            onPauseRequest();
        }
        // no changing format in readonly mode
        if (format === EventFormat.MARKDOWN) {
            if (!readonly) {
                onDetailChange(EventFormat.TEXT, details);
            } else {
                openMarkdownModal();
            }
        } else if (!readonly) {
            onDetailChange(EventFormat.MARKDOWN, details);
            openMarkdownModal();
        }
    };

    return (
        <Box style={{width: "100%"}} ref={ref} {...others} >
            <TextInput
                ref={inputRef}
                className={cx(classes.detailInput)}
                classNames={{
                    input: cx(isMarkdown && classes.readOnly, (isMarkdown || readonly) && classes.inputEllipsis)
                }}
                size="xs"
                label={showLabel ? "Event Detail" : ""}
                placeholder={readonly || isMarkdown ? getMarkdownSummary(details) : "Enter details"}
                defaultValue={readonly || isMarkdown ? "" : details}
                readOnly={isMarkdown || readonly}
                tabIndex={isMarkdown ? -1 : 0}
                onFocus={handleFocus}
                onChange={(e) => onDetailChange(EventFormat.TEXT, e.currentTarget.value)}
                onKeyDown={handleKeyDown}
                onClick={() => isMarkdown && handleClick()}
                rightSection={
                    <Box
                        className={classes.markdownButton}
                        style={{cursor: !readonly || isMarkdown ? "pointer" : "auto"}}
                        onClick={handleMarkdownButtonClick}
                        title={(readonly && !isMarkdown) ? "" : !readonly && isMarkdown ? "Change format to text" : "Open Markdown Editor"}
                    >
                        {format === EventFormat.MARKDOWN ? <IconMarkdown size="1em"/> : <IconFileText size={"1em"}/>}
                    </Box>
                }
            />
        </Box>
    );
});
